import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { connect } from 'react-redux';

import { ClientConfig } from 'client/configuration';
import { EdmundsAPI } from 'client/data/api/api-client';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EventToolbox } from 'client/utils/event-toolbox';
import { EVENTS } from 'client/utils/event-constants';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getIasData } from 'client/utils/get-ias-data';
import { isHeadlessBrowser } from 'client/utils/is-headless-browser';
import { logger } from 'client/utils/logger/logger';
import { ensureConsentResolved } from 'client/utils/ads-runtime/ensure-consent-resolved';
import { ensureGptLoaded } from 'client/utils/ads-runtime/ensure-gpt-loaded';
import { configureGptOnce } from 'client/utils/ads-runtime/configure-gpt-once';
import { ensurePbjsLoaded } from 'client/utils/ads-runtime/ensure-pbjs-loaded';
import {
  getRegisteredSlotCount,
  setHeaderBiddingAllowed,
  setPageTargeting,
  startInitialGptLoad,
  subscribeSlotRegistered,
} from 'client/utils/ads-runtime/gpt-runtime-v2';
import { refreshGptSlots } from 'client/utils/ads-runtime/refresh-gpt-slots';
import { getPbjsEnabled } from 'client/utils/header-bidding/get-pbjs-enabled';
import { isCcpaEligible } from 'client/utils/header-bidding/is-ccpa-eligible';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { getTotalAdUnitsToBeRenderedV2 } from 'site-modules/shared/components/ad-unit/ad-unit-v2';

import { getPageContext, getWtfContext } from './context/page-context';
import { getVisitorContext } from './context/visitor-context';
import { mapStateToProps as legacyMapStateToProps, publishTargeting, stateToPropsConfig } from './ads-context';
import { registerGptEventListenersOnce } from './utils/gpt-event-listeners';
import {
  dispatchImpressionViewableEvent,
  dispatchSlotRenderEndedEvent,
  dispatchSlotVisibilityChangedEvent,
} from './utils/gpt-slot-event-registry';

const RTT_TIMER_CALLS = [5000, 10000, 15000];

let iasAlreadyCalled = false;
let frData = '';
let iasKeywordData = '';

export function resetAdsContextV2ForTest() {
  iasAlreadyCalled = false;
  frData = '';
  iasKeywordData = '';
}

export class AdsContextV2UI extends Component {
  constructor(props) {
    super(props);
    this.rttCalls = 0;
    this.runtimeReady = false;
    this.initialLoadStarted = false;
    this.isHeaderBiddingAllowed = false;
    this.consentResolved = false;
    this.pbjsLoaded = false;
    this.state = {};
  }

  async componentDidMount() {
    EventToolbox.on(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    this.unsubscribeSlotRegistered = subscribeSlotRegistered(this.triggerLoadIfPossible);

    window.EDM = window.EDM || {};
    window.EDM.Venom = window.EDM.Venom || {};
    window.EDM.Venom.reactAds = true;
    window.EDM.Venom.adsLoadTriggered = false;

    await this.setTargeting();
    await this.bootstrapRuntime();
    this.runtimeReady = true;
    this.triggerLoadIfPossible();
  }

  async componentDidUpdate(prevProps) {
    await this.setTargeting();

    const privacyChanged = this.props.enableGpc && prevProps.privacy?.uspString !== this.props.privacy?.uspString;
    if (privacyChanged) {
      this.updatePrivacySettings();
      if (this.initialLoadStarted) {
        refreshGptSlots();
      }
    }

    this.triggerLoadIfPossible();
  }

  componentWillUnmount() {
    EventToolbox.off(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    if (this.unsubscribeSlotRegistered) {
      this.unsubscribeSlotRegistered();
    }
  }

  setTargeting = async () => {
    const {
      page,
      id,
      session,
      iasData,
      rttSegment,
      vehicle,
      region,
      legacy,
      dealer,
      userEnterZip,
      pageLocation,
      isCheckHeadlessBrowser,
      isEnableIAS,
      ads,
    } = this.props;

    const activeCampaigns = ExperimentUtil.debug.getVenomAssignmentsOnPage();
    const headlessBrowser = isCheckHeadlessBrowser && (await isHeadlessBrowser());
    const visitor = { id, session, iasData };

    if (!iasAlreadyCalled) {
      iasAlreadyCalled = true;
      const iasDataValue = isEnableIAS && (await getIasData(session, iasData));
      frData = get(iasDataValue, 'fr', null);
      iasKeywordData = get(iasDataValue, 'matchedKeyword', null);
    }

    const nextTargeting = {
      ...getWtfContext(activeCampaigns),
      ...getVisitorContext(visitor, region, userEnterZip),
      ...getPageContext(
        page,
        pageLocation,
        vehicle,
        legacy,
        dealer,
        headlessBrowser,
        frData,
        ads.utmAds,
        iasKeywordData
      ),
      aseg: rttSegment || this.state.rttSegment,
    };

    publishTargeting(nextTargeting);
    setPageTargeting(nextTargeting);
  };

  bootstrapRuntime = async () => {
    const { consentResolved } = await ensureConsentResolved();
    this.consentResolved = consentResolved;
    const { enableGpc, privacy, prebidReactAds } = this.props;
    const isLimitedAds = !consentResolved || Boolean(enableGpc && privacy?.doNotSell);

    await ensureGptLoaded(isLimitedAds);
    configureGptOnce({ enableGpc, privacy, limitedAds: isLimitedAds });
    registerGptEventListenersOnce({
      onSlotRenderEnded: dispatchSlotRenderEndedEvent,
      onSlotVisibilityChanged: dispatchSlotVisibilityChangedEvent,
      onImpressionViewable: dispatchImpressionViewableEvent,
    });

    const isPbjsEnabled = getPbjsEnabled({
      featureFlags: { prebidReactAds },
      visitor: { privacy },
    });

    if (!this.pbjsLoaded && this.consentResolved && isPbjsEnabled) {
      try {
        await ensurePbjsLoaded(ClientConfig.get('prebidJsUrl'), ClientConfig.get('prebidJsIntegrity'));
        this.pbjsLoaded = Boolean(window.pbjs);
      } catch (error) {
        logger.warn('Prebid failed to load; continuing with GPT-only ads.', error);
      }
    }

    this.updateHeaderBiddingPrivacy();
  };

  configurePrebidPrivacy = privacy => {
    if (this.pbjsLoaded && window.pbjs?.setConfig) {
      window.pbjs.setConfig({
        consentManagement: {
          gdpr: { enabled: false }, // Edmunds is US-only; no TCF CMP present
          usp: {
            cmpApi: 'static',
            consentData: {
              getUSPData: {
                uspString: privacy?.uspString || '1YNN',
              },
            },
          },
        },
        enableTIDs: true,
        userSync: {
          filterSettings: {
            iframe: {
              bidders: '*',
              filter: 'include',
            },
          },
        },
      });
    }
  };

  updateHeaderBiddingPrivacy = () => {
    const { privacy, prebidReactAds } = this.props;
    const isPbjsEnabled = getPbjsEnabled({
      featureFlags: { prebidReactAds },
      visitor: { privacy },
    });
    this.configurePrebidPrivacy(privacy);

    this.isHeaderBiddingAllowed = Boolean(
      this.consentResolved && isPbjsEnabled && isCcpaEligible(privacy?.uspString) && this.pbjsLoaded
    );
    window.EDM.Venom.isHeaderBiddingAllowed = this.isHeaderBiddingAllowed;
    setHeaderBiddingAllowed(this.isHeaderBiddingAllowed);
  };

  updatePrivacySettings = () => {
    this.updateHeaderBiddingPrivacy();
    const { privacy } = this.props;

    window.googletag.cmd.push(() => {
      window.googletag.pubads().setPrivacySettings({
        restrictDataProcessing: Boolean(privacy?.doNotSell),
        us_privacy: privacy?.uspString,
        limitedAds: Boolean(privacy?.doNotSell),
      });
    });
  };

  handleResolveRttSegment = async () => {
    EventToolbox.off(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    await this.resolveRttSegment();
  };

  resolveRttSegment = async () => {
    const { disableUseRTTModel, disableRTTForE2e } = this.props;
    if (disableUseRTTModel && !disableRTTForE2e) {
      const rttSegment = await EdmundsAPI.fetchJson(`/user-db/v1/segments/${this.props.id}`, {
        retries: 0,
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache' },
      }).catch(() => []);

      this.setState({ rttSegment });
      if (this.rttCalls < RTT_TIMER_CALLS.length && !rttSegment?.length) {
        setTimeout(this.resolveRttSegment, RTT_TIMER_CALLS[this.rttCalls]);
        this.rttCalls += 1;
      }
    }
  };

  triggerLoadIfPossible = () => {
    if (!this.runtimeReady || this.initialLoadStarted) {
      return;
    }

    const { rttSegment, isAuthenticated, hasUserData, enableGpc, disableUseRTTModel, disableRTTForE2e, region } =
      this.props;
    const waitingForRttSegment =
      (!disableUseRTTModel && rttSegment === null) ||
      (disableUseRTTModel && !disableRTTForE2e && !this.state.rttSegment);
    const waitingForUserData = enableGpc && (isAuthenticated === null || (isAuthenticated && !hasUserData));

    if (waitingForRttSegment || waitingForUserData || !region) {
      return;
    }

    const expectedSlots = getTotalAdUnitsToBeRenderedV2();
    const registeredSlotCount = getRegisteredSlotCount();
    if (!registeredSlotCount || registeredSlotCount < expectedSlots) {
      return;
    }

    this.initialLoadStarted = true;
    window.EDM.Venom.adsLoadTriggered = true;
    startInitialGptLoad({ isHeaderBiddingAllowed: this.isHeaderBiddingAllowed });
  };

  render() {
    const { pageAdsList, pageAdsListVerified, pageAdsListSeparatedCalls, pageAdsListAdCalls } = this.props;
    return (
      <ErrorBoundary>
        <div
          data-ad-names={Array.from(pageAdsListSeparatedCalls || pageAdsList || []).join()}
          data-ad-model-verified={pageAdsListVerified ? Array.from(pageAdsListVerified).join() : 'verifying'}
          data-ad-model-calls={pageAdsListAdCalls}
          hidden
        />
      </ErrorBoundary>
    );
  }
}

AdsContextV2UI.propTypes = {
  ads: PropTypes.shape({ utmAds: PropTypes.shape({}) }),
  page: PropTypes.shape({ name: PropTypes.string, category: PropTypes.string }),
  pageLocation: PropTypes.shape({ search: PropTypes.string }),
  legacy: PropTypes.shape({ pageName: PropTypes.string, categoryName: PropTypes.string }),
  vehicle: PropTypes.shape({}),
  dealer: PropTypes.shape({}),
  region: PropTypes.shape({}),
  id: PropTypes.string,
  session: PropTypes.string,
  iasData: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  hasUserData: PropTypes.bool,
  privacy: PropTypes.shape({ doNotSell: PropTypes.bool, uspString: PropTypes.string }),
  enableGpc: PropTypes.bool,
  prebidReactAds: PropTypes.bool,
  rttSegment: PropTypes.arrayOf(PropTypes.string),
  userEnterZip: PropTypes.bool,
  isCheckHeadlessBrowser: PropTypes.bool,
  isEnableIAS: PropTypes.bool,
  pageAdsList: PropTypes.arrayOf(PropTypes.string),
  pageAdsListVerified: PropTypes.arrayOf(PropTypes.string),
  pageAdsListSeparatedCalls: PropTypes.arrayOf(PropTypes.string),
  pageAdsListAdCalls: PropTypes.number,
  disableUseRTTModel: PropTypes.bool,
  disableRTTForE2e: PropTypes.bool,
};

AdsContextV2UI.defaultProps = {
  ads: {},
  page: {},
  pageLocation: {},
  legacy: {},
  vehicle: {},
  dealer: {},
  region: undefined,
  id: undefined,
  session: undefined,
  iasData: null,
  isAuthenticated: null,
  hasUserData: false,
  privacy: {},
  enableGpc: false,
  prebidReactAds: false,
  rttSegment: null,
  userEnterZip: false,
  isCheckHeadlessBrowser: false,
  isEnableIAS: false,
  pageAdsList: null,
  pageAdsListVerified: null,
  pageAdsListSeparatedCalls: null,
  pageAdsListAdCalls: null,
  disableUseRTTModel: false,
  disableRTTForE2e: false,
};

export const mapStateToProps = state => ({
  ...legacyMapStateToProps(state),
  prebidReactAds: Boolean(state?.featureFlags?.prebidReactAds),
});

export const AdsContextV2 = connect(mapStateToProps)(connectToModel(AdsContextV2UI, stateToPropsConfig));
